<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ resolvedTitle }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div v-if="pipelineId" class="buttons-tab">
        <v-btn
          v-if="hasPermissions('deals-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="openCreateDealModal"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="deals-table"
              height="500"
              :name="resolvedTableName"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :filters="visibleFilters"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="serverDataOptions"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
              @filters-change="handleFiltersChange"
            >
              <template
                v-for="(header, index) in columnHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span
                  :key="index"
                  v-if="header.value === 'options'"
                  class="d-flex align-center justify-center flex-nowrap"
                >
                  <v-btn
                    icon
                    color="indigo"
                    @click="openDealViewModal(item.item.id)"
                  >
                    <v-icon dark>mdi-eye</v-icon>
                  </v-btn>
                </span>
                <template v-else-if="header.value === 'products'">
                  <div
                    v-if="getProductLabels(item.item).length"
                    class="d-flex flex-wrap justify-center align-center"
                  >
                    <v-chip
                      v-for="(label, labelIndex) in getProductLabels(item.item)"
                      :key="`product-${item.item.id}-${labelIndex}`"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ label }}
                    </v-chip>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'suppliers'">
                  <div
                    v-if="getCompanyNames(item.item.suppliers).length"
                    class="d-flex flex-wrap justify-center align-center"
                  >
                    <v-chip
                      v-for="(name, nameIndex) in getCompanyNames(item.item.suppliers)"
                      :key="`supplier-${item.item.id}-${nameIndex}`"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ name }}
                    </v-chip>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'clients'">
                  <div
                    v-if="getCompanyNames(item.item.clients).length"
                    class="d-flex flex-wrap justify-center align-center"
                  >
                    <v-chip
                      v-for="(name, nameIndex) in getCompanyNames(item.item.clients)"
                      :key="`client-${item.item.id}-${nameIndex}`"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ name }}
                    </v-chip>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'owners'">
                  <div
                    v-if="getOwnerNames(item.item.owners).length"
                    class="d-flex flex-wrap justify-center align-center"
                  >
                    <v-chip
                      v-for="(name, nameIndex) in getOwnerNames(item.item.owners)"
                      :key="`owner-${item.item.id}-${nameIndex}`"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ name }}
                    </v-chip>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'loading_dates'">
                  <div
                    v-if="getLoadingDates(item.item).length"
                    class="d-flex flex-wrap justify-center align-center loading-dates-chips"
                  >
                    <v-chip
                      v-for="loadingDate in getLoadingDates(item.item)"
                      :key="loadingDate.id"
                      x-small
                      outlined
                      color="primary"
                      class="ma-0 mr-1 mb-1"
                    >
                      {{ formatLoadingDate(loadingDate) }}
                    </v-chip>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'code'">
                  {{ formatDealCode(item.item) }}
                </template>
                <template v-else-if="header.value === 'deal_type.name'">
                  <v-chip
                    v-if="item.item.deal_type && item.item.deal_type.name"
                    x-small
                    outlined
                    :color="getDealTypeChipColor(item.item.deal_type)"
                  >
                    {{ item.item.deal_type.name }}
                  </v-chip>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'incoterm.name'">
                  {{ (item.item.incoterm && item.item.incoterm.name) || "-" }}
                </template>
                <template v-else-if="header.value === 'is_paid'">
                  <v-chip
                    x-small
                    :color="item.item.is_paid ? 'success' : 'grey'"
                    dark
                  >
                    {{
                      item.item.is_paid
                        ? $t("DEALS.MODAL.PAID_YES")
                        : $t("DEALS.MODAL.PAID_NO")
                    }}
                  </v-chip>
                </template>
                <template v-else>
                  {{ item.value || "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <deal-modal
      :open="dealModalOpen"
      :update-deal-id="updateDealId"
      :pipeline-stage-id="pipelineStageId"
      @close="closeDealModal"
      @saved="onDealSaved"
      @updated="onDealUpdated"
    />
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import DealModal from "@/views/pages/deals/partials/DealModal";
import { GET_DEALS, GET_DEAL_YEAR_OPTIONS } from "@/store/deals.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_DEAL_TYPE_OPTIONS } from "@/store/dealTypes.module";
import { GET_INCOTERM_OPTIONS } from "@/store/incoterms.module";
import { GET_DEAL_STATUS_OPTIONS } from "@/store/dealStatuses.module";
import { GET_MEATS, GET_DESCRIPTIONS } from "@/store/mappingValues.module";
import { GET_PIPELINE_OPTIONS, GET_PIPELINE } from "@/store/pipelines.module";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import DealsService from "@/common/deals.service";

export default {
  name: "DealsTable",
  components: {
    Table,
    DealModal,
  },
  props: {
    pipelineId: {
      type: [Number, String],
      default: null,
    },
    tableName: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      tab: "tab-1",
      userStatePrepared: false,
      isProcessing: false,
      blinkRowItemKey: null,
      tableItemsMethod: GET_DEALS,
      allFilters: [],
      availableMeats: [],
      availableDescriptions: [],
      defaultPipelineStageId: null,
      loadedPipelineName: "",
      dealModalOpen: false,
      updateDealId: null,
      pipelineStageId: null,
    };
  },
  computed: {
    resolvedTitle() {
      return this.title || this.loadedPipelineName || this.$t("DEALS.TITLE");
    },
    resolvedTableName() {
      if (this.tableName) {
        return this.tableName;
      }

      if (this.pipelineId) {
        return `pipeline-${this.pipelineId}-deals-table`;
      }

      return "deals-table";
    },
    serverDataOptions() {
      const params = {};

      if (this.pipelineId) {
        params.pipeline_id = parseInt(this.pipelineId, 10);
      }

      return {
        method: this.tableItemsMethod,
        params,
      };
    },
    headers() {
      return [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "6%",
        },
        {
          text: this.$t("DEALS.MODAL.DEAL_NAME"),
          value: "name",
          align: "center",
          width: "14%",
        },
        {
          text: this.$t("DEALS.MODAL.DEAL_NUMBER"),
          value: "code",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("DEALS.MODAL.ORGANIZATION"),
          value: "organization.name",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("DEALS.MODAL.OWNERS"),
          value: "owners",
          sortable: false,
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "8%",
        },
      ];
    },
    hiddenHeaders() {
      const headers = [
        {
          text: this.$t("DEALS.MODAL.YEAR_CREATED"),
          value: "year_created",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("DEALS.MODAL.SUPPLIERS"),
          value: "suppliers",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("DEALS.MODAL.CLIENTS"),
          value: "clients",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("DEALS.MODAL.PRODUCTS"),
          value: "products",
          sortable: false,
          align: "center",
          width: "16%",
        },
        {
          text: this.$t("DEALS.TABLE.PIPELINE"),
          value: "pipeline_stage.pipeline.name",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("PIPELINES.STAGE_NAME"),
          value: "pipeline_stage.name",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("DEALS.MODAL.TYPE"),
          value: "deal_type.name",
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("DEALS.MODAL.INCOTERM"),
          value: "incoterm.name",
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("DEALS.MODAL.LOCATION"),
          value: "location",
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("DEALS.MODAL.STATUS"),
          value: "deal_status.name",
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("DEALS.MODAL.IS_PAID"),
          value: "is_paid",
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("DEALS.MODAL.CONTAINER_ARRIVAL_DATE"),
          value: "container_arrival_date",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("DEALS.MODAL.LOADING_DATES"),
          value: "loading_dates",
          sortable: false,
          align: "center",
          width: "12%",
        },
      ];

      if (this.pipelineId) {
        return headers.filter((header) => header.value !== "pipeline_stage.pipeline.name");
      }

      return headers;
    },
    columnHeaders() {
      return [...this.headers, ...this.hiddenHeaders];
    },
    visibleFilters() {
      if (this.pipelineId) {
        return this.allFilters.filter((filter) => filter.key !== "pipeline_id");
      }

      return this.allFilters;
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.resolvedTableName}.blinkRowItemKey`,
      null
    );

    const selectedHiddenHeadersKey = `table-state.${this.resolvedTableName}.selectedHiddenHeaders`;
    const selectedHiddenHeadersInitializedKey = `table-state.${this.resolvedTableName}.selectedHiddenHeadersInitialized`;

    if (!userStateService.getState(selectedHiddenHeadersInitializedKey, false)) {
      userStateService.setState(selectedHiddenHeadersKey, [...this.hiddenHeaders]);
      userStateService.setState(selectedHiddenHeadersInitializedKey, true);
    }

    this.initFilters();
    this.loadFilterOptions().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    getLoadingDates(deal) {
      return Array.isArray(deal?.loading_dates) ? deal.loading_dates : [];
    },
    getCompanyNames(companies) {
      if (!Array.isArray(companies) || !companies.length) {
        return [];
      }

      return companies.map((item) => item.name).filter(Boolean);
    },
    getOwnerNames(owners) {
      if (!Array.isArray(owners) || !owners.length) {
        return [];
      }

      return owners.map((item) => item.name || item.label).filter(Boolean);
    },
    formatLoadingDate(loadingDate) {
      return DealsService.formatLoadingDate(loadingDate);
    },
    getProductLabels(deal) {
      return DealsService.getProductLabels(deal);
    },
    initFilters() {
      this.allFilters = [
        {
          key: "organization_id",
          label: this.$t("DEALS.MODAL.ORGANIZATION"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "supplier_id",
          label: this.$t("DEALS.MODAL.SUPPLIERS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "client_id",
          label: this.$t("DEALS.MODAL.CLIENTS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "pipeline_id",
          label: this.$t("DEALS.TABLE.PIPELINE"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "pipeline_stage_id",
          label: this.$t("PIPELINES.STAGE_NAME"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "deal_type_id",
          label: this.$t("DEALS.MODAL.TYPE"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "incoterm_id",
          label: this.$t("DEALS.MODAL.INCOTERM"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "deal_status_id",
          label: this.$t("DEALS.MODAL.STATUS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "year_created",
          label: this.$t("DEALS.MODAL.YEAR_CREATED"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "meat_id",
          label: this.$t("DEALS.MODAL.MEAT"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "description_id",
          label: this.$t("DEALS.MODAL.DESCRIPTION"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "is_paid",
          label: this.$t("DEALS.MODAL.IS_PAID"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [
            { id: 1, label: this.$t("DEALS.MODAL.PAID_YES") },
            { id: 0, label: this.$t("DEALS.MODAL.PAID_NO") },
          ],
        },
        {
          key: "container_arrival_date_from",
          label: `${this.$t("DEALS.MODAL.CONTAINER_ARRIVAL_DATE")} (${this.$t("GENERAL.LABELS.START_DATE")})`,
          type: "date",
        },
        {
          key: "container_arrival_date_to",
          label: `${this.$t("DEALS.MODAL.CONTAINER_ARRIVAL_DATE")} (${this.$t("GENERAL.LABELS.END_DATE")})`,
          type: "date",
        },
        {
          key: "loading_date_from",
          label: `${this.$t("DEALS.MODAL.LOADING_DATES")} (${this.$t("GENERAL.LABELS.START_DATE")})`,
          type: "date",
        },
        {
          key: "loading_date_to",
          label: `${this.$t("DEALS.MODAL.LOADING_DATES")} (${this.$t("GENERAL.LABELS.END_DATE")})`,
          type: "date",
        },
      ];
    },
    findFilter(key) {
      return this.allFilters.find((filter) => filter.key === key);
    },
    loadFilterOptions() {
      this.isProcessing = true;

      const requests = [
        this.$store
          .dispatch(GET_ORGANIZATIONS_OPTIONS)
          .then((response) => {
            this.findFilter("organization_id").items = response.data || [];
          }),
        this.$store
          .dispatch(GET_COMPANIES_OPTIONS, { active: true })
          .then((response) => {
            const companies = response.data || [];
            this.findFilter("supplier_id").items = companies;
            this.findFilter("client_id").items = companies;
          }),
        this.$store.dispatch(GET_DEAL_TYPE_OPTIONS).then((response) => {
          this.findFilter("deal_type_id").items = response.data || [];
        }),
        this.$store.dispatch(GET_INCOTERM_OPTIONS).then((response) => {
          this.findFilter("incoterm_id").items = response.data || [];
        }),
        this.$store.dispatch(GET_DEAL_STATUS_OPTIONS).then((response) => {
          this.findFilter("deal_status_id").items = response.data || [];
        }),
        this.$store.dispatch(GET_DEAL_YEAR_OPTIONS).then((response) => {
          this.findFilter("year_created").items = response.data || [];
        }),
        this.$store.dispatch(GET_MEATS).then((response) => {
          this.availableMeats = (response || []).map((meat) => ({
            id: meat.id,
            label: meat.value,
          }));
          this.findFilter("meat_id").items = this.availableMeats;
          this.updateDescriptionFilterItems();
        }),
        this.$store.dispatch(GET_DESCRIPTIONS).then((response) => {
          this.availableDescriptions = response || [];
          this.updateDescriptionFilterItems();
        }),
      ];

      if (!this.pipelineId) {
        requests.push(
          this.$store.dispatch(GET_PIPELINE_OPTIONS).then((response) => {
            this.findFilter("pipeline_id").items = response.data || [];
          })
        );
      } else {
        requests.push(this.loadPipelineStageOptions(this.pipelineId));
      }

      return Promise.all(requests)
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    handleFiltersChange(filters) {
      this.updateDescriptionFilterItems(filters?.meat_id);
    },
    updateDescriptionFilterItems(selectedMeatIds = []) {
      const selectedIds = Array.isArray(selectedMeatIds) ? selectedMeatIds : [];
      const descriptions = selectedIds.length
        ? this.availableDescriptions.filter((description) =>
            selectedIds.some(
              (id) => Number(id) === Number(description.group_value_id)
            )
          )
        : [];

      this.findFilter("description_id").items = descriptions
        .map((description) => {
          const meat = this.availableMeats.find(
            (item) => Number(item.id) === Number(description.group_value_id)
          );

          if (!meat) {
            return null;
          }

          return {
            id: description.id,
            label: `${description.value} (${meat.label})`,
          };
        })
        .filter(Boolean);
    },
    loadPipelineStageOptions(pipelineId) {
      return this.$store.dispatch(GET_PIPELINE, pipelineId).then((response) => {
        const stages = Array.isArray(response.stages) ? response.stages : [];
        const sortedStages = [...stages].sort(
          (a, b) => Number(a.order || 0) - Number(b.order || 0)
        );

        this.loadedPipelineName = response.name || "";
        this.defaultPipelineStageId = sortedStages[0]?.id || null;
        this.findFilter("pipeline_stage_id").items = sortedStages.map((stage) => ({
          id: stage.id,
          label: stage.name,
        }));
        this.$emit("pipeline-loaded", { name: this.loadedPipelineName });
      });
    },
    //malko nenujno, ama realno ne prechi. AI-q towa kaza
    normalizeYearOptions(years) {
      const values = Array.isArray(years) ? years : [];

      const normalizedYears = values
        .map((year) => {
          const value = year && typeof year === "object" ? year.year_created || year.year : year;
          const numericYear = Number(value);

          return Number.isInteger(numericYear) ? numericYear : null;
        })
        .filter((year) => year !== null);

      return [...new Set(normalizedYears)]
        .sort((a, b) => b - a)
        .map((year) => ({ id: year, label: String(year) }));
    },
    openCreateDealModal() {
      this.updateDealId = null;
      this.pipelineStageId = this.defaultPipelineStageId;
      this.dealModalOpen = true;
    },
    openDealViewModal(id) {
      if (!id) {
        return;
      }

      userStateService.setState(
        `table-state.${this.resolvedTableName}.blinkRowItemKey`,
        id
      );

      this.pipelineStageId = null;
      this.updateDealId = id;
      this.dealModalOpen = true;
    },
    closeDealModal() {
      this.dealModalOpen = false;
      this.updateDealId = null;
      this.pipelineStageId = null;
    },
    onDealSaved() {
      this.refresh();
    },
    onDealUpdated() {
      this.refresh();
    },
    refresh() {
      if (this.$refs["deals-table"]) {
        this.$refs["deals-table"].fetchServerData();
      }
    },
    formatDealCode(deal) {
      return DealsService.formatDealCode(deal);
    },
    getDealTypeChipColor(dealType) {
      return DealsService.getDealTypeChipColor(dealType);
    },
  },
};
</script>
